import { MouseEvent as MouseEventReact, useMemo, useState } from 'react';
import { cx } from '@emotion/css';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useGoBack } from '../../../../../../hooks/use-go-back';
import { IconTypes } from '../../../../../common/icon';
import { useStyles } from './user-menu.styles';
import { IconButton } from '../../../../../common/icon-button';
import { DropdownMenu, IMenuItem } from '../../../../../common/dropdown-menu';
import { useLogout } from '../../../../../../graphql/user/actions/logout';
import { InternalLinks } from '../../../../../../constants';
import { IUserMenuProps } from './user-menu.models';
import { VPStatus } from '../../../../../../hooks/use-vision-profile-data';

export const UserMenu = ({
  visionProfileStatus, orders, isOrders, isKids,
}: IUserMenuProps): JSX.Element => {
  const classes = useStyles();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.up('md'));
  const [dropDownAnchorEl, setDropDownAnchorEl] = useState<HTMLElement | null>(null);
  const { logout } = useLogout();
  const { hasPreviousLocationState } = useGoBack();
  const isShowOrders = !!orders?.ordersByAuthId?.length && isOrders;

  const menuItems: IMenuItem[] = useMemo(() => [
    {
      name: 'header.menuItem.findOptics',
      icon: IconTypes.map,
      to: InternalLinks.ecpLocator,
      state: hasPreviousLocationState,
      testId: 'menu-find-optics', // Don't remove testId it's used by YaMetrica
    },
    {
      name: 'header.menuItem.wallet',
      icon: IconTypes.wallet,
      to: InternalLinks.myBonusesPage,
      state: hasPreviousLocationState,
      testId: 'menu-wallet', // Don't remove testId it's used by YaMetrica
    },
    {
      name: 'header.menuItem.lenses',
      icon: IconTypes.lenses,
      to: InternalLinks.myLensesPage,
      state: hasPreviousLocationState,
      testId: 'menu-lenses', // Don't remove testId it's used by YaMetrica
    },
    {
      name: 'header.menuItem.orders',
      icon: IconTypes.shoppingBag,
      to: InternalLinks.myOrdersPage,
      state: hasPreviousLocationState,
      hidden: !isShowOrders,
      testId: 'menu-orders', // Don't remove testId it's used by YaMetrica
    },
    {
      name: 'header.menuItem.myProfile',
      icon: IconTypes.user,
      to: InternalLinks.profilePage,
      state: hasPreviousLocationState,
      testId: 'menu-my-profile', // Don't remove testId it's used by YaMetrica
    },
    {
      name: isKids ? 'kidsPage.title' : 'kidsProfile.add',
      icon: IconTypes.userKids,
      to: isKids ? InternalLinks.forKids : InternalLinks.createProfile,
      state: hasPreviousLocationState,
      testId: isKids ? 'menu-kids-page-title' : 'menu-kids-page-add', // Don't remove testId it's used by YaMetrica
    },
    {
      name: 'header.menuItem.myAcuvueLife',
      icon: IconTypes.eyeWithEyelashes,
      to: InternalLinks.visionProfile,
      state: hasPreviousLocationState,
      hidden: visionProfileStatus === VPStatus.INCOMPLETE,
    },
    {
      name: 'header.menuItem.logout',
      icon: IconTypes.exit,
      value: 'logout',
      testId: 'menu-logout', // Don't remove testId it's used by YaMetrica
    },
  ], [hasPreviousLocationState, orders]);

  const handleOpenMenuButtonClick = (e: MouseEventReact<HTMLElement>) => {
    setDropDownAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setDropDownAnchorEl(null);
  };

  const handleMenuItemClick = (value: string | null) => {
    if (value === 'logout') {
      logout();
    }

    handleMenuClose();
  };

  const userMenuIconType = (): IconTypes => {
    let type;
    if (isTablet) {
      type = IconTypes.login;
    } else if (dropDownAnchorEl) {
      type = IconTypes.closeMenu;
    } else {
      type = IconTypes.menu;
    }
    return type;
  };

  return (
    <>
      <IconButton
        iconProps={{ type: userMenuIconType() }}
        onClick={handleOpenMenuButtonClick}
        className={cx(
          { [classes.dropDownMenuOpen]: isTablet && !!dropDownAnchorEl },
          { [classes.dropDownMenu]: isTablet },
        )}
      />
      <DropdownMenu
        items={menuItems}
        anchorEl={dropDownAnchorEl}
        onClick={handleMenuItemClick}
        onClose={handleMenuClose}
      />
    </>
  );
};
